<template>
  <LoadingContainer :is-loading="isLoading">
    <div class="card">
      <div class="card-header">
        <div class="card-header-title">
          <div class="title is-6 is-marginless">
            Promo Cards
            <span class="tag ml-2">
              {{ items.length }}
            </span>
          </div>
        </div>
        <div class="card-header-icon">
          <router-link
            :to="{ name: 'content.home.promo-cards.add' }"
            class="button"
          >
            <span class="icon is-small">
              <i class="fa fa-plus" />
            </span>
            <span>Προσθήκη</span>
          </router-link>
        </div>
      </div>

      <div class="card-content">
        <div class="columns">
          <div class="column is-full">
            <div class="table-wrapper">
              <table
                v-if="items.length"
                class="table is-bordered is-striped is-marginless is-fullwidth"
              >
                <thead>
                  <tr>
                    <th class="has-text-centered" />
                    <th class="has-text-centered">#</th>
                    <th>Εικόνα</th>
                    <th>Τίτλος (EL)</th>
                    <th>Τίτλος (EN)</th>
                    <th>Link</th>
                    <th class="has-text-centered">Εμφανές</th>
                    <th class="has-text-centered">Ημ/νια Δημ.</th>
                    <th class="has-text-centered">Ημ/νια Ενημ.</th>
                    <th class="hidden-print">Ενέργειες</th>
                  </tr>
                </thead>

                <draggable
                  v-model="items"
                  :move="canMove"
                  tag="tbody"
                  handle=".drag-handle"
                  @change="onReorder"
                >
                  <tr v-for="(item, idx) in items" :key="String(item.id)">
                    <!-- Drag -->
                    <td class="has-text-centered">
                      <button
                        v-tooltip="'Σύρετε για αναδιάταξη'"
                        class="button is-small is-white drag-handle"
                        type="button"
                      >
                        <span class="icon is-small">
                          <i class="fa fa-bars" />
                        </span>
                      </button>
                    </td>

                    <!-- Index -->
                    <td class="has-text-centered">{{ idx + 1 }}</td>

                    <!-- Thumbnail -->
                    <td style="width: 80px">
                      <figure v-if="item.image_url" class="image">
                        <img :src="item.image_url" alt="thumb" />
                      </figure>
                      <span v-else>—</span>
                    </td>

                    <!-- Titles -->
                    <td>
                      <router-link
                        :to="{
                          name: 'content.home.promo-cards.edit',
                          params: { id: String(item.id) },
                        }"
                      >
                        {{ item.title_el }}
                      </router-link>
                    </td>
                    <td>{{ item.title_en }}</td>

                    <!-- Link -->
                    <td class="link-cell">
                      <a :href="item.link" target="_blank" rel="noopener">
                        {{ item.link }}
                      </a>
                    </td>

                    <!-- Active toggle -->
                    <td class="has-text-centered">
                      <ToggleButton
                        :value="!!item.is_active"
                        :sync="true"
                        :color="{ checked: '#22A684', unchecked: '#ff3860' }"
                        :width="30"
                        :height="15"
                        @change="toggleActive(item, $event)"
                      />
                      <p class="help is-size-7 mt-1">Έως 2 ενεργές κάρτες</p>
                    </td>

                    <!-- Dates -->
                    <td class="has-text-centered">
                      {{ formatDateTime(item.created_at) }}
                    </td>
                    <td class="has-text-centered">
                      {{ formatDateTime(item.updated_at) }}
                    </td>

                    <!-- Actions -->
                    <td class="hidden-print">
                      <div class="buttons are-small">
                        <router-link
                          :to="{
                            name: 'content.home.promo-cards.edit',
                            params: { id: String(item.id) },
                          }"
                          class="button is-small"
                          title="Επεξεργασία"
                        >
                          <span class="icon is-small">
                            <i class="fa fa-pencil" />
                          </span>
                        </router-link>

                        <button
                          class="button is-small is-outlined"
                          type="button"
                          @click="askToDelete(item)"
                        >
                          <span class="icon is-small">
                            <i class="fa fa-trash" />
                          </span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </draggable>
              </table>

              <h4 v-else class="title is-4 has-text-centered">
                Δε βρέθηκαν στοιχεία
              </h4>
            </div>
          </div>
        </div>
      </div>

      <!-- Delete confirmation -->
      <Confirmation
        :model-name="selected ? selected.title_el : ''"
        :is-open="isConfirmOpen"
        description="Είστε βέβαιος για τη διαγραφή;"
        @closeModal="onConfirmClose"
      />
    </div>
  </LoadingContainer>
</template>

<script>
import draggable from 'vuedraggable';
import request from '@/utils/request';
import LoadingContainer from '@/views/components/LoadingContainer';
import Confirmation from '@/views/components/Confirmation';

export default {
  name: 'PromoCardsList',
  components: { draggable, LoadingContainer, Confirmation },

  data() {
    return {
      isLoading: false,
      items: [],
      originalSnapshot: [],
      isConfirmOpen: false,
      selected: null,
    };
  },

  metaInfo: { title: 'Promo Cards' },

  created() {
    this.fetch();
  },

  methods: {
    async fetch() {
      this.isLoading = true;
      try {
        const { data } = await request.get('/home/promo-cards');

        let list = [];
        if (Array.isArray(data)) {
          list = data;
        } else if (data && Array.isArray(data.data)) {
          list = data.data;
        }

        this.items = list;
        this.snapshot();
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'To αίτημα απέτυχε',
        });
      }
      this.isLoading = false;
    },

    snapshot() {
      this.originalSnapshot = JSON.parse(JSON.stringify(this.items));
    },

    canMove(evt) {
      // Allow drag between any rows (server enforces contiguous order)
      const dc = evt && evt.draggedContext ? evt.draggedContext : null;
      const rc = evt && evt.relatedContext ? evt.relatedContext : null;
      const di = dc && typeof dc.index === 'number' ? Number(dc.index) : NaN;
      const ri = rc && typeof rc.index === 'number' ? Number(rc.index) : NaN;
      return !Number.isNaN(di) && !Number.isNaN(ri);
    },

    async onReorder() {
      const payload = this.items.map((it, idx) => ({
        id: String(it.id),
        order: idx,
      }));

      try {
        await request.patch('/home/promo-cards/reorder', {
          cards: payload,
        });

        this.items.forEach((it, idx) => {
          this.$set(it, 'order', idx);
        });
        this.snapshot();

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η αναδιάταξη ολοκληρώθηκε',
        });
      } catch (e) {
        this.items = JSON.parse(JSON.stringify(this.originalSnapshot));
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          'To αίτημα απέτυχε';
        this.$notify({ type: 'error', title: 'Αποτυχία', text: msg });
      }
    },

    async toggleActive(item, evt) {
      const hasValue =
        evt && Object.prototype.hasOwnProperty.call(evt, 'value');
      const next = hasValue ? !!evt.value : !item.is_active;
      const prev = !!item.is_active;

      this.$set(item, 'is_active', next);

      try {
        await request.patch(`/home/promo-cards/${String(item.id)}`, {
          is_active: next,
        });

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: next
            ? 'Η κάρτα είναι τώρα εμφανής.'
            : 'Η κάρτα δεν είναι πλέον εμφανής.',
        });
      } catch (e) {
        this.$set(item, 'is_active', prev);

        const raw =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          'To αίτημα απέτυχε';

        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text:
            raw.indexOf('promo cards can be active') !== -1
              ? 'Επιτρέπονται έως 2 ενεργές κάρτες. Απενεργοποίησε πρώτα μία από τις ενεργές.'
              : raw,
        });
      }
    },

    askToDelete(item) {
      this.selected = item;
      this.isConfirmOpen = true;
    },

    async onConfirmClose(confirmed) {
      this.isConfirmOpen = false;
      if (!confirmed || !this.selected) return;

      try {
        const { data } = await request.delete(
          `/home/promo-cards/${String(this.selected.id)}`,
        );
        if (data && data.error) {
          throw new Error(data.message || 'Server error');
        }

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η κάρτα διαγράφηκε',
        });

        await this.fetch();
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'To αίτημα απέτυχε',
        });
      }
      this.selected = null;
    },

    formatDateTime(v) {
      if (!v) return '-';
      const s = String(v).trim();
      const parts = s.replace('T', ' ').split(' ');
      if (parts.length) {
        const ymd = (parts[0] || '').split('-');
        const y = ymd[0];
        const m = ymd[1];
        const d = ymd[2];
        const timePart = (parts[1] || '').slice(0, 5);
        if (y && m && d) {
          const pad = n => String(n).padStart(2, '0');
          return `${pad(d)}-${pad(m)}-${y} ${timePart || '00:00'}`;
        }
      }
      const ts = Date.parse(s);
      if (!Number.isNaN(ts)) {
        const dt = new Date(ts);
        const pad = n => String(n).padStart(2, '0');
        return `${pad(dt.getDate())}-${pad(
          dt.getMonth() + 1,
        )}-${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
      }
      return s;
    },
  },
};
</script>

<style scoped>
.table-wrapper {
  position: relative;
}

.drag-handle {
  cursor: move;
}

.image img {
  object-fit: cover;
}

.link-cell {
  max-width: 420px;
  white-space: normal;
}

.link-cell a {
  display: inline-block;
  overflow-wrap: anywhere;
  word-break: break-word;
}
</style>
